import { appendQueryStringToUrl } from '@/utils/url-helper';
import { resolveFallbackLink } from './resolve-fallback-link';

interface BuildLinkHrefParams {
  internalLink?: string | null;
  externalLink?: string | null;
  fallbackLink?: string | null;
  destinationTopics?: { path: string }[] | null;
  country?: string | null;
  locale: string;
  urlParamsQueryString: string;
}

interface BuildLinkHrefResult {
  href?: string;
  // True when the visitor was rerouted to the country-restriction fallback link,
  // in which case any anchor from the original destination must be dropped.
  isRestrictedFallback: boolean;
}

// Resolves the final href for an internal/external CTA link, applying the
// folder-overview cleanup, the country-restriction fallback and url-parameters.
export function buildLinkHref({
  internalLink,
  externalLink,
  fallbackLink,
  destinationTopics,
  country,
  locale,
  urlParamsQueryString,
}: BuildLinkHrefParams): BuildLinkHrefResult {
  const overviewSuffix = `/${process.env.NEXT_PUBLIC_CRYSTALLIZE_FOLDER_OVERVIEW_NAME}`;

  // if the internal link ends with the folder overview name we remove it to
  // avoid duplicate content
  const parsedInternalLink = internalLink?.endsWith(overviewSuffix)
    ? internalLink.slice(0, -overviewSuffix.length)
    : internalLink;

  // When the internal-link destination is restricted in the visitor's country,
  // route to the CMS fallback link (or the locale index) instead of the dead page.
  const restrictedFallbackHref = internalLink
    ? resolveFallbackLink({
      destinationTopics,
      country,
      fallbackLink,
      localeIndexPath: `/${locale}/`,
    })
    : null;

  let href = restrictedFallbackHref || externalLink || parsedInternalLink || undefined;

  if (href) {
    href = appendQueryStringToUrl(href, urlParamsQueryString);
  }

  return { href, isRestrictedFallback: Boolean(restrictedFallbackHref) };
}
